import Papa from "papaparse";
import { supabase } from "./supabaseClient";
import { parisDateTimeToUtcIso } from "./parisDateTime";

/**
 * Interface pour une ligne CSV Airtable
 */
export interface AirtableCSVRow {
  Société: string;
  État: string;
  "Nombre de PAX": string;
  "Activités Souhaitées": string;
  "Autre Activité": string;
  "Date de l'évènement": string;
  "Date Alternative": string;
  "Budget Approx": string;
  "Type d'évènement": string;
  "Autre Type d'évènement": string;
  "Précisions complémentaires": string;
  Adresse: string;
  "Nom du contact référent": string;
  "Adresse email": string;
  "Numéro de téléphone": string;
  Devis: string;
  "Notes Internes": string;
  "Relance État": string;
  "Date de Rappel": string;
  "Devis Signé ": string; // Note: espace à la fin dans le CSV !
  Centres: string;
  "Total HT / par PAX": string;
  "Acompte réglé": string;
  "Date de création": string;
  "Code Postal": string;
  "Facture Finale": string;
  "Différence HT Facture - Devis Signé": string;
}

/**
 * Interface pour une ligne transformée prête pour Supabase
 */
export interface TransformedCase {
  title: string;
  company_name: string | null;
  status: string;
  pax: number | null;
  participants: number | null;
  activities: string[] | null;
  event_date: string | null;
  alt_date: string | null;
  budget: number | null;
  occasion: string | null;
  client_notes: string | null;
  address: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  internal_notes: string | null;
  relance_status: string | null;
  reminder_date: string | null;
  quote_signed: boolean;
  center: string | null;
  budget_per_person: number | null;
  deposit_invoice: string | null;
  postal_code: string | null;
  final_invoice: string | null;
  invoice_quote_diff: number | null;
  old_airtable_quotes: string | null;
  source: string;
  airtable_id: string;
  created_at: string | null;
}

/**
 * Mapping des statuts Airtable → Supabase
 */
const STATUS_MAPPING: Record<string, string> = {
  "Supprimé": "Supprimé",
  "Rejeté": "Rejeté",
  "Non Traité": "Non traité",
  "Non traité": "Non traité",
  "À relancer ce jour": "À relancer ce jour",
  "Devis envoyé": "Devis envoyé",
  "À relancer": "À relancer",
  "Déjà relancé": "Déjà relancé",
  "Signé": "Signé",
  "Acompte reçu": "Acompte reçu",
  "Versement complet": "Versement complet",
};

/**
 * Parser une date française (DD/MM/YYYY HH:mm) ou américaine (M/D/YYYY h:mmam/pm) vers ISO
 */
function parseDate(dateStr: string): string | null {
  if (!dateStr || dateStr.trim() === "") return null;

  try {
    // Format français : DD/MM/YYYY HH:mm ou DD/MM/YYYY
    const frMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
    if (frMatch) {
      const [, day, month, year, hour = "00", minute = "00"] = frMatch;
      return parisDateTimeToUtcIso(
        `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
        `${hour.padStart(2, '0')}:${minute}`
      );
    }

    // Format américain : M/D/YYYY h:mmam/pm
    const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(am|pm))?$/i);
    if (usMatch) {
      const [, month, day, year, hourStr = "0", minute = "00", period = "am"] = usMatch;
      let hour = parseInt(hourStr);
      if (period.toLowerCase() === "pm" && hour < 12) hour += 12;
      if (period.toLowerCase() === "am" && hour === 12) hour = 0;
      return parisDateTimeToUtcIso(
        `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
        `${hour.toString().padStart(2, '0')}:${minute}`
      );
    }

    // Fallback: tenter le parsing natif
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  } catch {
    return null;
  }
}

/**
 * Parser un montant (enlever €, HT, espaces, etc.)
 */
function parseAmount(amountStr: string): number | null {
  if (!amountStr || amountStr.trim() === "") return null;

  try {
    // Enlever €, HT, espaces, caractères spéciaux
    const cleaned = amountStr
      .replace(/[€HTht\s]/g, "")
      .replace(/,/g, ".")
      .trim();

    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * Splitter un nom complet en prénom + nom
 */
function splitName(fullName: string): { firstName: string | null; lastName: string | null } {
  if (!fullName || fullName.trim() === "") return { firstName: null, lastName: null };

  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: null };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

/**
 * Splitter les activités (séparées par virgule ou retour à la ligne)
 */
function parseActivities(activities: string, otherActivity: string): string[] | null {
  const allActivities: string[] = [];

  if (activities && activities.trim()) {
    const split = activities.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    allActivities.push(...split);
  }

  if (otherActivity && otherActivity.trim()) {
    allActivities.push(otherActivity.trim());
  }

  return allActivities.length > 0 ? allActivities : null;
}

/**
 * Mapper le statut Airtable → Supabase
 */
function mapStatus(airtableStatus: string): string {
  return STATUS_MAPPING[airtableStatus] || "Non traité";
}

/**
 * Générer un ID unique à partir des données (pour éviter doublons)
 */
function generateAirtableId(row: AirtableCSVRow, index: number): string {
  const key = `${row.Société || ""}-${row["Adresse email"] || ""}-${row["Date de création"] || index}`;
  // Simple hash pour créer un ID unique
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    const char = key.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `airtable-${Math.abs(hash)}-${index}`;
}

/**
 * Transformer une ligne CSV Airtable vers le format Supabase
 */
export function transformAirtableRow(row: AirtableCSVRow, index: number): TransformedCase {
  const { firstName, lastName } = splitName(row["Nom du contact référent"] || "");
  const activities = parseActivities(row["Activités Souhaitées"] || "", row["Autre Activité"] || "");

  return {
    // Champs obligatoires
    title: row.Société || `Import Airtable ${index + 1}`,
    company_name: row.Société || null,
    status: mapStatus(row.État || ""),
    source: "Airtable Import",
    airtable_id: generateAirtableId(row, index),

    // Champs numériques
    pax: row["Nombre de PAX"] ? parseInt(row["Nombre de PAX"]) : null,
    participants: row["Nombre de PAX"] ? parseInt(row["Nombre de PAX"]) : null,
    budget: parseAmount(row["Budget Approx"] || ""),
    budget_per_person: parseAmount(row["Total HT / par PAX"] || ""),
    invoice_quote_diff: parseAmount(row["Différence HT Facture - Devis Signé"] || ""),

    // Arrays
    activities,

    // Dates
    event_date: parseDate(row["Date de l'évènement"] || ""),
    alt_date: parseDate(row["Date Alternative"] || ""),
    reminder_date: parseDate(row["Date de Rappel"] || ""),
    created_at: parseDate(row["Date de création"] || ""),

    // Strings
    occasion: row["Type d'évènement"] || row["Autre Type d'évènement"] || null,
    client_notes: row["Précisions complémentaires"] || null,
    address: row.Adresse || null,
    client_first_name: firstName,
    client_last_name: lastName,
    client_email: row["Adresse email"] || null,
    client_phone: row["Numéro de téléphone"] || null,
    internal_notes: row["Notes Internes"] || null,
    relance_status: row["Relance État"] || null,
    center: row.Centres || null,
    postal_code: row["Code Postal"] || null,
    final_invoice: row["Facture Finale"] || null,
    old_airtable_quotes: row.Devis || null,
    deposit_invoice: row["Acompte réglé"] || null,

    // Booleans
    quote_signed: (row["Devis Signé "] || "").trim().toLowerCase() === "oui" || (row["Devis Signé "] || "").toLowerCase() === "true",
  };
}

/**
 * Parser un fichier CSV Airtable
 */
export function parseAirtableCSV(file: File): Promise<TransformedCase[]> {
  return new Promise((resolve, reject) => {
    Papa.parse<AirtableCSVRow>(file, {
      header: true,
      skipEmptyLines: true,
      transform: (value) => value.trim(),
      complete: (results) => {
        try {
          const transformed = results.data.map((row, index) => transformAirtableRow(row, index));
          resolve(transformed);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}

/**
 * Importer des cases par batch dans Supabase (SANS téléchargement des devis)
 */
export async function importCasesBatch(cases: TransformedCase[], batchSize = 500) {
  const results = {
    total: cases.length,
    success: 0,
    errors: 0,
    duplicates: 0,
    errorDetails: [] as Array<{ index: number; error: string; data: TransformedCase }>,
  };

  // Obtenir l'ID de l'utilisateur connecté
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || null;

  for (let i = 0; i < cases.length; i += batchSize) {
    const batch = cases.slice(i, i + batchSize);

    // Ajouter created_by à chaque case
    const batchWithUser = batch.map(c => ({
      ...c,
      created_by: userId,
    }));

    const { data, error } = await supabase
      .from("cases")
      .insert(batchWithUser)
      .select();

    if (error) {
      // Gérer les erreurs de duplicata
      if (error.code === "23505") {
        // Duplicate key violation
        results.duplicates += batch.length;
      } else {
        results.errors += batch.length;
        results.errorDetails.push({
          index: i,
          error: error.message,
          data: batch[0], // Premier élément du batch pour debug
        });
      }
    } else {
      results.success += data?.length || 0;
    }
  }

  return results;
}
